import React, { useState } from 'react';
import { FileArchive, Calendar, Download, AlertCircle, CheckCircle2, Loader2, FileText, Users } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import UserSelector from './UserSelector';
import { dmarc_api } from '../../services/auth';

// Base que será /api/dmarc en producción
const API_BASE = process.env.REACT_APP_API_BASE || "/api/dmarc";
// Para el PDF (endpoint /reports/dmarc.pdf) necesitamos la raíz sin /dmarc
const API_ROOT = (process.env.REACT_APP_API_BASE || "/api/dmarc").replace(
  /\/dmarc\/?$/,
  "",
);

export default function ReportPanel() {
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState('idle');
  const [errorMessage, setErrorMessage] = useState('');

  const handleGenerateReport = async () => {
    if (selectedUsers.length === 0) {
      setErrorMessage('Por favor, seleccione al menos un usuario.');
      setStatus('error');
      return;
    }
    if (!startDate || !endDate) {
      setErrorMessage('Por favor, seleccione un rango de fechas válido.');
      setStatus('error');
      return;
    }

    setLoading(true);
    setStatus('idle');

    try {   
      // Usamos for...of para que el try/catch capture errores de red y espere cada petición
      for (const u of selectedUsers) {
        const response = await dmarc_api.post(
          '/reports/dmarc.zip',
          {
            id: u.id,
            desde: startDate,
            hasta: endDate
          },
          { responseType: 'blob' } // Importante para recibir el archivo ZIP en binario
        );

        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `reportes_${u.correo}_${startDate}_${endDate}.zip`);
        document.body.appendChild(link);
        link.click();
        link.parentNode?.removeChild(link);
      }
      
      setStatus('success');
    } catch (error) {
      console.error('Error generando reporte:', error);
      setErrorMessage('Hubo un error al generar el reporte. Intente nuevamente.');
      setStatus('error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-8">
      {/* Header Section */}
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="space-y-2"
      >
        <div className="flex items-center gap-3">
          <div className="p-3 bg-cyan-500/10 rounded-2xl">
            <FileArchive className="text-cyan-500" size={28} />
          </div>
          <div>
            <h1 className="text-2xl font-black text-slate-900 dark:text-white tracking-tight">
              Panel de Reportes
            </h1>
            <p className="text-sm text-slate-500 font-medium">
              Genera y descarga archivos comprimidos con la actividad detallada de tus clientes.
            </p>
          </div>
        </div>
      </motion.div>

      {/* Main Configuration Card */}
      <motion.div 
        initial={{ opacity: 0, scale: 0.98 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 0.1 }}
        className="bg-white dark:bg-slate-900 rounded-3xl border border-slate-100 dark:border-slate-800 shadow-xl shadow-slate-200/50 dark:shadow-none overflow-hidden"
      >
        <div className="p-8 space-y-8">
          {/* User Selection Section */}
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <Users size={16} className="text-cyan-500" />
              <h2 className="text-xs font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest">
                Configuración de Usuarios
              </h2>
            </div>
            <UserSelector 
              selectedUsers={selectedUsers} 
              onChange={setSelectedUsers} 
            />
          </div>

          {/* Date Range Section */}
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <Calendar size={16} className="text-cyan-500" />
              <h2 className="text-xs font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest">
                Rango de Fechas
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-1.5">
                <label className="block text-[10px] font-black text-gray-400 dark:text-slate-500 uppercase tracking-widest">
                  Fecha de Inicio
                </label>
                <div className="relative">
                  <input 
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className="w-full px-4 py-2.5 border border-gray-200 dark:border-slate-800 rounded-xl text-sm bg-transparent dark:text-slate-200 dark:[color-scheme:dark] outline-none focus:ring-2 focus:ring-cyan-500/50 transition-all"
                  />
                </div>
              </div>
              <div className="space-y-1.5">
                <label className="block text-[10px] font-black text-gray-400 dark:text-slate-500 uppercase tracking-widest">
                  Fecha de Término
                </label>
                <div className="relative">
                  <input 
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    className="w-full px-4 py-2.5 border border-gray-200 dark:border-slate-800 rounded-xl text-sm bg-transparent dark:text-slate-200 dark:[color-scheme:dark] outline-none focus:ring-2 focus:ring-cyan-500/50 transition-all"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Action Footer */}
          <div className="pt-4 flex flex-col items-center gap-4">
            <button
              onClick={handleGenerateReport}
              disabled={loading}
              className={`
                w-full md:w-auto min-w-[240px] px-8 py-4 rounded-2xl font-black text-sm uppercase tracking-widest
                flex items-center justify-center gap-3 transition-all active:scale-95
                ${loading 
                  ? 'bg-slate-100 dark:bg-slate-800 text-slate-400 cursor-not-allowed' 
                  : 'bg-cyan-600 hover:bg-cyan-700 text-white shadow-lg shadow-cyan-500/20'
                }
              `}
            >
              {loading ? (
                <>
                  <Loader2 className="animate-spin" size={18} />
                  Procesando...
                </>
              ) : (
                <>
                  <Download size={18} />
                  Generar y Descargar ZIP
                </>
              )}
            </button>

            <AnimatePresence mode="wait">
              {status === 'success' && (
                <motion.div 
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0 }}
                  className="flex items-center gap-2 text-emerald-600 dark:text-emerald-400 font-bold text-xs bg-emerald-50 dark:bg-emerald-500/10 px-4 py-2 rounded-full border border-emerald-100 dark:border-emerald-500/20"
                >
                  <CheckCircle2 size={14} />
                  Reporte generado con éxito. La descarga ha comenzado.
                </motion.div>
              )}
              {status === 'error' && (
                <motion.div 
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0 }}
                  className="flex items-center gap-2 text-red-600 dark:text-red-400 font-bold text-xs bg-red-50 dark:bg-red-500/10 px-4 py-2 rounded-full border border-red-100 dark:border-red-500/20"
                >
                  <AlertCircle size={14} />
                  {errorMessage}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>

        {/* Info Banner */}
        <div className="bg-slate-50 dark:bg-slate-800/50 p-6 border-t border-slate-100 dark:border-slate-800">
          <div className="flex items-start gap-4">
            <div className="p-2 bg-white dark:bg-slate-900 rounded-xl border border-slate-100 dark:border-slate-800">
              <FileText size={16} className="text-slate-400" />
            </div>
            <div className="space-y-1">
              <h4 className="text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest">
                Nota sobre el formato
              </h4>
              <p className="text-xs text-slate-500 leading-relaxed">
                El archivo ZIP contendrá reportes individuales en formato PDF para cada usuario seleccionado dentro del rango de fechas especificado. El procesamiento puede tardar dependiendo de la cantidad de datos.
              </p>
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
}
